/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { computed, IComputedValue } from 'mobx';
import { transformLabels, TransformLabelsReturn } from 'src/luis/modules/labeling/utils/transformLabels';
import { UtterancePredictionsStore } from 'src/luis/projects/utterances/hooks/useUtterances';
import { UtteranceStore } from 'src/luis/projects/utterances/stores/utteranceStore';
import { Utterance } from 'src/luis/projects/utterances/types/Utterance';
import { utteranceDenormalizer } from 'src/luis/projects/utterances/utils/utteranceDenormalizer';
import { LuisId } from 'src/luis/types/Resource';
import { EntityStoreHouse } from 'src/luis/types/stores/entities/EntityStoreHouse';
import { PhraseListStoreHouse } from 'src/luis/types/stores/features/PhraseListStoreHouse';
import { IntentStoreHouse } from 'src/luis/types/stores/intents/IntentStoreHouse';
import { SuggestedUtteranceStoreHouse } from 'src/luis/types/stores/utterances/SuggestedUtteranceStoreHouse';

export type SuggestedUtteranceObservable = {
    id: LuisId;
    store: UtterancePredictionsStore;
    computed: IComputedValue<readonly [Utterance, TransformLabelsReturn]>;
};

type GenerateUtterancesObsInput = {
    localUtterancesMapStore: Map<LuisId, UtteranceStore>;
    suggestedUtteranceStoreHouse: SuggestedUtteranceStoreHouse;
    entityStoreHouse: EntityStoreHouse;
    intentStoreHouse: IntentStoreHouse;
    phraseListStoreHouse: PhraseListStoreHouse;
};

export const generateSuggestedUtterancesObservables = (input: GenerateUtterancesObsInput): SuggestedUtteranceObservable[] => {
    const { localUtterancesMapStore, entityStoreHouse, intentStoreHouse, phraseListStoreHouse, suggestedUtteranceStoreHouse } = input;

    if (localUtterancesMapStore.size === 0) {
        return [];
    }

    return suggestedUtteranceStoreHouse.items.map<SuggestedUtteranceObservable>(utterance => {
        const utterancePrediction: UtterancePredictionsStore = {
            utteranceStore: localUtterancesMapStore.get(utterance.id),
            predictionStore: utterance
        };

        return {
            id: utterance.id,
            store: utterancePrediction,
            computed: computed(() => {
                const denormalizedUtterance = utteranceDenormalizer({
                    utterancePrediction,
                    intentStores: intentStoreHouse.items,
                    entityStores: entityStoreHouse.items,
                    phraseListStores: phraseListStoreHouse.items
                });

                return <const>[
                    denormalizedUtterance,
                    transformLabels({
                        tokenizedText: denormalizedUtterance.tokenizedText,
                        userLabels: denormalizedUtterance.labeledEntities,
                        predictions: denormalizedUtterance.predictedEntities
                    })
                ];
            })
        };
    });
};
